<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import { useCatalogPage } from '@/composition/catalog/catalog-page-context';
  import type { ICatalogEditorTarget } from '@/composition/catalog/catalog-page-state';
  import {
    provideProductEditor,
    useProductEditor,
  } from '@/composition/catalog/use-product-editor';
  import type { ICatalogMenuEntry } from '@/domain/catalog/catalog-menus';
  import { menuChoiceName, menuChoices } from '@/domain/catalog/menu-choices';
  import V2CatalogEditorBanner from '../shared/V2CatalogEditorBanner.vue';
  import V2CatalogEditorFooter from '../shared/V2CatalogEditorFooter.vue';
  import V2CatalogEditorHeader from '../shared/V2CatalogEditorHeader.vue';
  import V2CatalogInheritanceNotes from '../shared/V2CatalogInheritanceNotes.vue';
  import V2CatalogBasicsSection from './V2CatalogBasicsSection.vue';
  import V2CatalogCustomerInfoSection from './V2CatalogCustomerInfoSection.vue';
  import V2CatalogMoreSettingsSection from './V2CatalogMoreSettingsSection.vue';
  import V2CatalogOptionGroupsSection from './V2CatalogOptionGroupsSection.vue';
  import V2CatalogPriceSection from './V2CatalogPriceSection.vue';
  import V2CatalogProductAvailability from './V2CatalogProductAvailability.vue';

  /**
   * The product editor (plan §5.12): header, notices, the sections in the
   * order of DESIGN §4.2, «Eliminar producto», and the footer. Create mode
   * shows only «Lo básico» and «Precio». One instance per target: the host
   * keys it, so nothing leaks between products.
   */
  const props = defineProps<{
    target: ICatalogEditorTarget;
    /** Docked beside the list (the header shows its own ✕). */
    docked: boolean;
  }>();

  const emit = defineEmits<{ (e: 'close'): void }>();

  type TEditorAction = 'view' | 'duplicate' | 'delete';

  const editor = useProductEditor(props.target);
  provideProductEditor(editor);

  const { data, access, online, actions, state } = useCatalogPage();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  const form = computed(() => editor.form.value);
  const item = computed(() => editor.item.value);
  const category = computed(() => editor.category.value);

  const title = computed(
    () =>
      form.value?.name.trim() ||
      item.value?.name ||
      t('menuPage.editor.newProduct')
  );

  /** «Menú principal», or «Menú: Almuerzo» (the switcher's wording). */
  const menuLabel = computed(() => {
    const name = menuChoiceName(
      menuChoices(data.menus.value, data.restaurant.value?.settings ?? null),
      state.menuId
    );
    if (name.kind === 'main') return t('menuPage.menus.main');
    return t('menuPage.menus.current', {
      name:
        name.kind === 'named'
          ? name.name
          : t('menuPage.menus.unnamed', { n: name.position }),
    });
  });

  const subtitle = computed(() => {
    if (editor.isNew)
      return category.value
        ? t('menuPage.editor.createSubtitle', { category: category.value.name })
        : undefined;
    if (data.multipleMenus.value && data.menus.value.length)
      return t('menuPage.editor.menuSubtitle', { menu: menuLabel.value });
    return category.value?.name;
  });

  const branchName = computed(() => {
    const id = data.branchId.value;
    return (
      data.storeBranches.value.find((branch) => branch.id === id)?.name ?? ''
    );
  });

  const entries = computed<ICatalogMenuEntry<TEditorAction>[]>(() => {
    if (editor.isNew || !item.value || editor.deleted.value) return [];
    const list: ICatalogMenuEntry<TEditorAction>[] = [];
    if (data.restaurant.value?.stringId)
      list.push({
        key: 'view',
        labelKey: 'menuPage.rowMenu.view',
        icon: 'open-in-new',
        danger: false,
        write: false,
        plan: null,
      });
    // The copy is created hidden everywhere (see `rowMenuEntries`).
    if (access.value.canManage && access.value.canHideEverywhere)
      list.push({
        key: 'duplicate',
        labelKey: 'menuPage.rowMenu.duplicate',
        icon: 'content-copy',
        danger: false,
        write: true,
        plan: null,
      });
    if (access.value.canManage)
      list.push({
        key: 'delete',
        labelKey: 'menuPage.rowMenu.delete',
        icon: 'delete-outline',
        danger: true,
        write: true,
        plan: null,
      });
    return list;
  });

  function onAction(key: string) {
    const current = item.value;
    if (!current) return;
    if (key === 'view') actions.viewInMenu(current);
    else if (key === 'duplicate') void actions.duplicate(current);
    else if (key === 'delete') void actions.deleteItems([current]);
  }

  /** «Guardar» needs something the role may change. */
  const footerReadOnly = computed(
    () =>
      editor.readOnly.value &&
      !editor.canEditAvailability.value &&
      !access.value.canTranslate
  );

  const failedText = computed(() => {
    const failed = editor.failedExtras.value;
    if (!failed.length) return null;
    if (failed.length > 1) return t('menuPage.editor.failed.both');
    return t(`menuPage.editor.failed.${failed[0]}`);
  });
</script>

<template>
  <div class="v2-catalog-product-editor">
    <V2CatalogEditorHeader
      :title="title"
      :subtitle="subtitle"
      :entries="entries"
      :online="online"
      :show-close="docked"
      @select="onAction"
      @close="emit('close')"
    />

    <div class="editor-body">
      <template v-if="form">
        <V2CatalogEditorBanner
          v-if="editor.deleted.value"
          tone="red"
          :text="t('menuPage.editor.deleted')"
        />
        <V2CatalogEditorBanner
          v-else-if="editor.remoteChanged.value"
          tone="amber"
          :text="t('menuPage.editor.remoteChanged')"
        />
        <V2CatalogEditorBanner
          v-if="failedText"
          tone="red"
          :text="failedText"
          :action-text="t('menuPage.retry')"
          :action-disabled="editor.saving.value || !online"
          @action="editor.retryExtras()"
        />

        <V2CatalogInheritanceNotes
          v-if="category && !editor.isNew"
          :category="category"
          :branch-id="data.branchId.value"
          :branch-name="branchName"
        />

        <V2CatalogBasicsSection />
        <V2CatalogPriceSection />

        <p v-if="editor.isNew" class="create-note">
          {{ t('menuPage.editor.createNote') }}
        </p>
        <template v-else>
          <V2CatalogProductAvailability />
          <V2CatalogOptionGroupsSection />
          <V2CatalogCustomerInfoSection />
          <V2CatalogMoreSettingsSection />
          <div
            v-if="access.canManage && !editor.deleted.value"
            class="editor-end"
          >
            <PosUiButton
              color="error"
              variant="ghost"
              icon="delete-outline"
              :disabled="!online || editor.saving.value"
              @click="onAction('delete')"
              >{{ t('menuPage.editor.deleteProduct') }}</PosUiButton
            >
          </div>
        </template>
      </template>

      <div v-else class="editor-loading" aria-hidden="true">
        <PosUiSkeleton class="loading-card is-tall" />
        <PosUiSkeleton class="loading-card" />
        <PosUiSkeleton class="loading-card" />
      </div>
    </div>

    <V2CatalogEditorFooter
      :change-count="editor.changeCount.value"
      :saving="editor.saving.value"
      :saved-flash="editor.savedFlash.value"
      :read-only="footerReadOnly"
      :create="editor.isNew"
      :retry="!!failedText && !editor.dirty.value"
      :online="online"
      :save-disabled="editor.deleted.value || !form"
      @close="emit('close')"
      @discard="editor.discard()"
      @save="editor.save()"
      @save-another="editor.save({ another: true })"
      @retry="editor.retryExtras()"
    />
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-product-editor {
    @apply tw-flex tw-h-full tw-min-h-0 tw-flex-col tw-bg-canvas;

    .editor-body {
      @apply tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-p-3;
      overscroll-behavior-y: contain;
    }

    .create-note {
      @apply tw-mx-1 tw-mb-0 tw-mt-3 tw-text-sm tw-leading-relaxed tw-text-neutral-500;
    }

    .editor-end {
      @apply tw-mt-3 tw-flex tw-justify-center tw-pb-2;
    }

    .editor-loading {
      .loading-card {
        @apply tw-mb-3 tw-h-32 tw-w-full tw-rounded-xl;

        &.is-tall {
          @apply tw-h-48;
        }
      }
    }
  }
</style>
